import { posthog } from 'posthog-js'
import { useEffect, useRef, useState } from 'react'
import { Helmet } from 'react-helmet'
import { useSearchParams } from 'react-router'

import { Button } from '@/components/ui/button'
import { PageLoader } from '@/components/ui/PageLoader'
import { isDevelopment } from '../../config/config'
import { resetAnalytics } from '../../lib/analytics'
import { safeNextPath } from '../../lib/safeNextPath'
import {
  announceAccountChange,
  redeemSignInTicket,
  signInErrorMessage,
  useAuthStatus,
} from '../../models/auth'
import { useCurrentUser } from '../../models/user'

const LINK_REJECTED = 'This sign-in link is invalid or has expired.'

/** The ticket is one-use, so it comes off the address bar, history and the next page's referrer. */
function dropTicketFromUrl() {
  window.history.replaceState(window.history.state, '', new URL(window.location.href).pathname)
}

/** Replace, not push: back from the destination must not land on this spent link. */
function leaveTo(target: string) {
  window.location.replace(target)
}

export function Impersonate() {
  const [searchParams] = useSearchParams()
  const [error, setError] = useState<string | null>(null)
  // Set when the visitor is signed in as a different account than the link
  // targets — they must confirm before we switch accounts.
  const [switchToLabel, setSwitchToLabel] = useState<string | null>(null)
  const [switching, setSwitching] = useState(false)
  const startedRef = useRef(false)

  const token = searchParams.get('token')
  // Default to /insure so IndexRedirect picks the landing page: application, quotes, insurance or dashboard.
  const target = safeNextPath(searchParams.get('next'), '/insure')
  const { data: authStatus } = useAuthStatus()
  const isSignedIn = Boolean(authStatus?.authenticated)
  // Who this browser is signed in as: the link's own account skips the switch, another one is named on it.
  const { user: currentUser, isError: currentUserFailed } = useCurrentUser({ enabled: isSignedIn })

  async function redeem() {
    dropTicketFromUrl()
    try {
      await redeemSignInTicket(token!)
    } catch (failure) {
      setSwitching(false)
      setError(signInErrorMessage(failure))
      return
    }
    if (!isDevelopment && searchParams.get('source') === 'staff') {
      posthog.register({ is_staff_session: true })
    }
    announceAccountChange()
    leaveTo(target)
  }

  useEffect(() => {
    if (startedRef.current) return
    if (!token) {
      startedRef.current = true
      setError(LINK_REJECTED)
      return
    }
    if (!authStatus || (isSignedIn && !currentUser && !currentUserFailed)) return
    startedRef.current = true

    if (!isSignedIn) {
      void redeem()
      return
    }
    // uid is the Insure user id the link was made for.
    if (currentUser && currentUser.id === searchParams.get('uid')) {
      dropTicketFromUrl()
      leaveTo(target)
      return
    }
    // Signed in as someone else, or the link names nobody: confirm before switching.
    setSwitchToLabel(searchParams.get('label') || 'the linked account')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, authStatus, isSignedIn, currentUser, currentUserFailed])

  function handleConfirmSwitch() {
    setSwitching(true)
    // The account changes underneath PostHog, so the old one's identity mustn't carry over.
    resetAnalytics()
    void redeem()
  }

  if (error) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 px-6 text-center">
        <p className="text-md text-ink-600">{error}</p>
        <Button href="/insure">Sign in</Button>
      </div>
    )
  }

  if (switchToLabel) {
    const currentLabel = currentUser?.displayLabel || 'another account'
    return (
      <>
        <Helmet>
          <title>Switch account</title>
        </Helmet>
        <div className="flex min-h-screen flex-col items-center justify-center gap-4 px-6 text-center">
          <div className="max-w-md space-y-2">
            <h1 className="text-lg font-semibold">Switch account?</h1>
            <p className="text-sm text-gray-600">
              You are currently signed in as <strong>{currentLabel}</strong>. Continuing will sign you out and
              log you in as <strong>{switchToLabel}</strong>.
            </p>
          </div>
          <Button
            className="text-md"
            loading={switching ? 'Switching…' : undefined}
            onClick={handleConfirmSwitch}
          >
            Continue as {switchToLabel}
          </Button>
        </div>
      </>
    )
  }

  return (
    <>
      <Helmet>
        <title>Login</title>
      </Helmet>
      <PageLoader persist label="Signing you in…" />
    </>
  )
}
